import React, { useState, useEffect } from 'react';
import { FiX, FiAlertTriangle } from 'react-icons/fi';
import { invoiceService } from '../../services/invoiceService';
import { toast } from 'react-hot-toast';
import { VoidInvoiceModal, SupersedeInvoiceModal } from '../admin/InvoiceStatusModals';
import { useTenant } from '../../context/TenantContext';
import { tenantDataService } from '../../services/tenantDataService';
import { formatCurrency, DEFAULT_CURRENCY } from '../../utils/currency';

// Helper function to format dates safely in dd/mm/yyyy format
function formatDate(dateValue) {
  if (!dateValue) return "";
  
  let date;
  
  // Handle Firestore Timestamp objects
  if (dateValue.seconds) {
    date = new Date(dateValue.seconds * 1000);
  }
  // Handle Date objects
  else if (dateValue instanceof Date) {
    date = dateValue;
  }
  // Handle string dates
  else if (typeof dateValue === 'string') {
    date = new Date(dateValue);
    if (isNaN(date)) return dateValue;
  }
  else {
    return String(dateValue);
  }
  
  // Format as dd/mm/yyyy
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  
  return `${day}/${month}/${year}`;
}

export default function InvoicePreviewModal({ bill, client, appointment, onClose, onRefresh, userRole }) {
  const { currentTenant } = useTenant();
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [showSupersedeModal, setShowSupersedeModal] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [supersedeReason, setSupersedeReason] = useState('');
  const [clinicSettings, setClinicSettings] = useState(null);

  useEffect(() => {
    const loadClinicSettings = async () => {
      try {
        const settings = await tenantDataService.getAll('settings');
        if (settings && settings.length > 0) {
          setClinicSettings(settings[0]);
        }
      } catch (error) {
        console.error('Error loading clinic settings:', error);
      }
    };

    if (currentTenant) {
      loadClinicSettings();
    }
  }, [currentTenant]);

  if (!bill) return null;

  const isAdmin = userRole === 'admin' || userRole === 'super_admin';
  const canModifyInvoice = isAdmin && (bill.status === 'paid' || bill.status === 'unpaid');

  const handleVoidInvoice = async () => {
    try {
      await invoiceService.voidInvoice(bill.id, voidReason);
      toast.success('Invoice voided successfully');
      setShowVoidModal(false);
      setVoidReason('');
      if (onRefresh) onRefresh();
      onClose();
    } catch (error) {
      toast.error('Failed to void invoice');
      console.error('Error voiding invoice:', error);
    }
  };

  const handleSupersedeInvoice = async () => {
    try {
      await invoiceService.supersedeInvoice(bill.id, '', supersedeReason);
      toast.success('Invoice superseded successfully');
      setShowSupersedeModal(false);
      setSupersedeReason('');
      if (onRefresh) onRefresh();
      onClose();
    } catch (error) {
      toast.error('Failed to supersede invoice');
      console.error('Error superseding invoice:', error);
    }
  };

  const handleRevertInvoice = async () => {
    const action = bill.status === 'void' ? 'revert this voided invoice' : 'revert this superseded invoice';
    if (window.confirm(`Are you sure you want to ${action} back to its original status?`)) {
      try {
        if (bill.status === 'void') {
          await invoiceService.revertVoidedInvoice(bill.id);
        } else if (bill.status === 'superseded') {
          await invoiceService.revertSupersededInvoice(bill.id);
        }
        toast.success('Invoice reverted successfully');
        if (onRefresh) onRefresh();
        onClose();
      } catch (error) {
        toast.error('Failed to revert invoice');
        console.error('Error reverting invoice:', error);
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center">
      <div className="bg-white max-w-4xl w-full mx-4 rounded-lg shadow-2xl relative print:p-0 print:shadow-none print:bg-white print:max-w-none print:mx-0 print:h-auto print:overflow-visible">
        <button
          className="absolute top-4 right-4 z-10 px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 print:hidden transition-colors"
          onClick={onClose}
        >
          <FiX className="w-4 h-4" />
        </button>
        
        {/* Professional Invoice Layout */}
        <div className="p-12 print:p-8 print:page-break-inside-avoid">
          {/* Header Section */}
          <div className="flex justify-between items-start mb-8">
            {/* Left: Clinic Logo and Name */}
            <div className="flex items-center space-x-4">
              {clinicSettings?.logo && (
                <img 
                  src={clinicSettings.logo} 
                  alt="Clinic Logo" 
                  className="w-16 h-16 object-contain"
                />
              )}
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {clinicSettings?.clinicName || currentTenant?.name || 'HealeHub Clinic'}
                </h1>
                <p className="text-sm text-gray-600 mt-1">Healthcare Services</p>
              </div>
            </div>
            
            {/* Right: Clinic Address */}
            <div className="text-right text-sm text-gray-600">
              {clinicSettings?.address && (
                <>
                  <div>{clinicSettings.address.street}</div>
                  <div>{clinicSettings.address.city}, {clinicSettings.address.state} {clinicSettings.address.zipCode}</div>
                  {clinicSettings.phone && <div>Tel: {clinicSettings.phone}</div>}
                  {clinicSettings.email && <div>Email: {clinicSettings.email}</div>}
                </>
              )}
            </div>
          </div>

          {/* Invoice Title and Details */}
          <div className="border-b-2 border-gray-200 pb-6 mb-8">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">INVOICE</h2>
                <div className="text-sm text-gray-600">
                  <div>Invoice #: <span className="font-semibold text-gray-900">{bill.invoiceNumber}</span></div>
                  <div>Date: <span className="font-semibold text-gray-900">{formatDate(bill.createdAt || bill.date)}</span></div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-600 mb-2">Status</div>
                <div className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-sm font-bold ${
                  bill.status === 'paid' ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' :
                  bill.status === 'unpaid' ? 'bg-red-100 text-red-800 border border-red-200' :
                  bill.status === 'void' ? 'bg-gray-100 text-gray-800 border border-gray-200' :
                  bill.status === 'superseded' ? 'bg-amber-100 text-amber-800 border border-amber-200' :
                  'bg-gray-100 text-gray-800 border border-gray-200'
                }`}>
                  {bill.status.toUpperCase()}
                </div>
              </div>
            </div>
          </div>

          {/* Bill To Section */}
          <div className="grid grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Bill To:</h3>
              <div className="text-gray-700">
                <div className="font-medium">{bill.clientName || bill.recipientName}</div>
                {bill.recipientEmail && <div className="text-sm text-gray-600">{bill.recipientEmail}</div>}
                {client?.address && (
                  <div className="text-sm text-gray-600 mt-2">
                    <div>{client.address.street}</div>
                    <div>{client.address.city}, {client.address.state} {client.address.zipCode}</div>
                  </div>
                )}
              </div>
            </div>
            
            {appointment && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Appointment Details:</h3>
                <div className="text-gray-700 text-sm">
                  <div><span className="font-medium">Date:</span> {formatDate(appointment.date)}</div>
                  <div><span className="font-medium">Time:</span> {appointment.time}</div>
                  <div><span className="font-medium">Service:</span> {appointment.type}</div>
                </div>
              </div>
            )}
          </div>

          {/* Services Table */}
          <div className="mb-8">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 font-semibold text-gray-900">Description</th>
                  <th className="text-left py-3 font-semibold text-gray-900">Date</th>
                  <th className="text-right py-3 font-semibold text-gray-900">Amount</th>
                </tr>
              </thead>
              <tbody>
                {bill.lineItems && bill.lineItems.map((li, idx) => (
                  <tr key={idx} className="border-b border-gray-100">
                    <td className="py-4 text-gray-700">{li.description}</td>
                    <td className="py-4 text-gray-600">{formatDate(li.date)}</td>
                    <td className="py-4 text-right font-medium text-gray-900">{formatCurrency(li.amount, bill.currency)}</td>
                  </tr>
                ))}
                
                {/* Subtotal */}
                <tr className="border-t border-gray-200">
                  <td colSpan={2} className="py-2 text-right font-medium text-gray-700">Subtotal:</td>
                  <td className="py-2 text-right font-medium text-gray-900">{formatCurrency(bill.subtotal || bill.total, bill.currency)}</td>
                </tr>
                
                {/* Discount */}
                {bill.discount > 0 && (
                  <tr>
                    <td colSpan={2} className="py-2 text-right font-medium text-gray-700">Discount ({bill.discount}%):</td>
                    <td className="py-2 text-right font-medium text-red-600">-{formatCurrency((Number(bill.subtotal || bill.total) * Number(bill.discount) / 100), bill.currency)}</td>
                  </tr>
                )}
                
                {/* Tax */}
                {bill.tax > 0 && (
                  <tr>
                    <td colSpan={2} className="py-2 text-right font-medium text-gray-700">Tax ({bill.tax}%):</td>
                    <td className="py-2 text-right font-medium text-gray-900">{formatCurrency((((Number(bill.subtotal || bill.total) - (Number(bill.subtotal || bill.total) * Number(bill.discount || 0) / 100)) * Number(bill.tax)) / 100), bill.currency)}</td>
                  </tr>
                )}
                
                {/* Total */}
                <tr className="border-t-2 border-gray-200">
                  <td colSpan={2} className="py-4 text-right font-bold text-gray-900 text-lg">Total:</td>
                  <td className="py-4 text-right font-bold text-gray-900 text-lg">{formatCurrency(bill.total, bill.currency)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Payment Information */}
          <div className="grid grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Payment Information:</h3>
              <div className="text-sm text-gray-700 space-y-1">
                <div><span className="font-medium">Payment Type:</span> {bill.paymentType || 'Not specified'}</div>
                {bill.paymentDate && <div><span className="font-medium">Paid on:</span> {formatDate(bill.paymentDate)}</div>}
                {bill.dueDate && <div><span className="font-medium">Due Date:</span> {formatDate(bill.dueDate)}</div>}
                {bill.voidedAt && <div><span className="font-medium text-red-600">Voided on:</span> {formatDate(bill.voidedAt)}</div>}
                {bill.voidReason && <div><span className="font-medium text-red-600">Void reason:</span> {bill.voidReason}</div>}
                {bill.supersededAt && <div><span className="font-medium text-amber-600">Superseded on:</span> {formatDate(bill.supersededAt)}</div>}
                {bill.supersededReason && <div><span className="font-medium text-amber-600">Supersede reason:</span> {bill.supersededReason}</div>}
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Invoice Details:</h3>
              <div className="text-sm text-gray-700 space-y-1">
                <div><span className="font-medium">Issued by:</span> {bill.createdBy || 'System'}</div>
                <div><span className="font-medium">Created:</span> {formatDate(bill.createdAt)}</div>
              </div>
            </div>
          </div>

          {/* Payment Details Footer */}
          {clinicSettings?.paymentDetails && (
            <div className="border-t-2 border-gray-200 pt-6 mt-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Payment Details:</h3>
              <div className="text-sm text-gray-700 space-y-1">
                {clinicSettings.paymentDetails.bankName && (
                  <div><span className="font-medium">Bank:</span> {clinicSettings.paymentDetails.bankName}</div>
                )}
                {clinicSettings.paymentDetails.accountNumber && (
                  <div><span className="font-medium">Account Number:</span> {clinicSettings.paymentDetails.accountNumber}</div>
                )}
                {clinicSettings.paymentDetails.sortCode && (
                  <div><span className="font-medium">Sort Code:</span> {clinicSettings.paymentDetails.sortCode}</div>
                )}
                {clinicSettings.paymentDetails.iban && (
                  <div><span className="font-medium">IBAN:</span> {clinicSettings.paymentDetails.iban}</div>
                )}
              </div>
            </div>
          )}

          {/* Legal Compliance Footer */}
          {clinicSettings?.invoiceCompliance && (
            <div className="border-t border-gray-200 pt-6 mt-6">
              <div className="text-xs text-gray-600 space-y-2">
                {/* Payment Terms */}
                {clinicSettings.invoiceCompliance.paymentTerms?.enabled && (
                  <div>
                    <span className="font-medium">Payment Terms:</span> Payment is due within {clinicSettings.invoiceCompliance.paymentTerms.dueDays || 30} days of invoice date. 
                    {clinicSettings.invoiceCompliance.paymentTerms.latePaymentRate && (
                      <span> Late payments may incur a {clinicSettings.invoiceCompliance.paymentTerms.latePaymentRate}% monthly charge.</span>
                    )}
                  </div>
                )}

                {/* Business Registration */}
                {clinicSettings.invoiceCompliance.businessRegistration?.enabled && clinicSettings.invoiceCompliance.businessRegistration?.number && (
                  <div>
                    <span className="font-medium">Business Registration:</span> {clinicSettings.invoiceCompliance.businessRegistration.number}
                  </div>
                )}

                {/* VAT/Tax ID */}
                {clinicSettings.invoiceCompliance.vatTaxId?.enabled && clinicSettings.invoiceCompliance.vatTaxId?.number && (
                  <div>
                    <span className="font-medium">VAT/Tax ID:</span> {clinicSettings.invoiceCompliance.vatTaxId.number}
                  </div>
                )}

                {/* Payment Methods */}
                {clinicSettings.invoiceCompliance.paymentMethods?.enabled && clinicSettings.invoiceCompliance.paymentMethods?.methods && (
                  <div>
                    <span className="font-medium">Payment Methods Accepted:</span> {clinicSettings.invoiceCompliance.paymentMethods.methods}
                  </div>
                )}

                {/* Contact & Dispute Resolution */}
                {clinicSettings.invoiceCompliance.contactDispute?.enabled && (
                  <div>
                    <span className="font-medium">For billing inquiries or disputes, contact:</span>
                    {clinicSettings.invoiceCompliance.contactDispute.email && (
                      <span> {clinicSettings.invoiceCompliance.contactDispute.email}</span>
                    )}
                    {clinicSettings.invoiceCompliance.contactDispute.phone && (
                      <span> | {clinicSettings.invoiceCompliance.contactDispute.phone}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-8 print:hidden">
          <div className="flex flex-wrap gap-3 justify-between items-center">
            <button
              className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow-sm hover:shadow-md"
              onClick={() => window.print()}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print / Save as PDF
            </button>
            {canModifyInvoice && (
              <div className="flex gap-3">
                <button
                  className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-lg text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200"
                  onClick={() => setShowVoidModal(true)}
                >
                  <FiX className="w-4 h-4 mr-2" />
                  Void Invoice
                </button>
                <button
                  className="inline-flex items-center px-4 py-2 border border-amber-300 text-sm font-medium rounded-lg text-amber-700 bg-amber-50 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-all duration-200"
                  onClick={() => setShowSupersedeModal(true)}
                >
                  <FiAlertTriangle className="w-4 h-4 mr-2" />
                  Supersede Invoice
                </button>
              </div>
            )}
            {(bill.status === 'void' || bill.status === 'superseded') && isAdmin && (
              <button
                className="inline-flex items-center px-4 py-2 border border-green-300 text-sm font-medium rounded-lg text-green-700 bg-green-50 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200"
                onClick={handleRevertInvoice}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                </svg>
                Revert Invoice
              </button>
            )}
          </div>
        </div>
      </div>

        <VoidInvoiceModal
          isOpen={showVoidModal}
          invoice={bill}
          voidReason={voidReason}
          setVoidReason={setVoidReason}
          onVoid={handleVoidInvoice}
          onClose={() => {
            setShowVoidModal(false);
            setVoidReason('');
          }}
        />

        <SupersedeInvoiceModal
          isOpen={showSupersedeModal}
          invoice={bill}
          supersedeReason={supersedeReason}
          setSupersedeReason={setSupersedeReason}
          onSupersede={handleSupersedeInvoice}
          onClose={() => {
            setShowSupersedeModal(false);
            setSupersedeReason('');
          }}
        />
      </div>
    </div>
  );
}
